import { Fragment } from 'react'

import { useRouter } from 'next/router'

import { useTranslation } from 'next-i18next'

import { ActivityMenu, BootcampPageUrlParamsType } from '~/bootcamp/types/bootcamp.types'
import useActivityPageNavigation from '~/bootcamp/hooks/useActivityPageNavigation'

import { useAppSelector } from '~/redux/hooks'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'

import { getLevelIndexShift } from '~/bootcamp/utils/bootcamp.utils'

import ActivityPageMenu from './ActivityPageMenu'

const ActivityNavigation = ({
  onMenuClick,
  isMenuCollapsed,
}: {
  onMenuClick: () => void
  isMenuCollapsed: boolean
}): React.ReactElement => {
  const { query } = useRouter()
  const { level } = query as unknown as BootcampPageUrlParamsType
  const activityMenu = useActivityPageNavigation()
  const activityMenuKeys = Object.keys(activityMenu)
  const { t } = useTranslation('bootcamp')

  const renderLevelPages = (
    { title, type, level: bootcampLevel, levelTag, index, icon }: ActivityMenu,
    idx: number,
  ): React.ReactElement => {
    return (
      <li className=" border-gray-divider last:border-none" key={`${type}-${level}-${idx}`}>
        <ActivityPageMenu
          icon={icon}
          index={index}
          level={bootcampLevel}
          levelTag={levelTag}
          onMenuClick={onMenuClick}
          title={title}
          type={type}
        />
      </li>
    )
  }

  const levelShift = getLevelIndexShift(activityMenu)

  return (
    <section className="p-4 bg-white rounded overflow-hidden">
      <div className=" text-black-light-fade">{t('menu')}</div>
      <nav>
        <ul className="mt-3 lg:mt-2 pb-4 grid gap-1">
          {!isMenuCollapsed &&
            activityMenuKeys.map((menuLevel, index) => {
              const numericLevelKey = parseInt(menuLevel, 10)

              const levelPages: ActivityMenu[] = activityMenu[numericLevelKey]

              const { levelTag } = levelPages[0]

              return (
                <Fragment key={`menu-${numericLevelKey}`}>
                  {levelTag && <Level value={index + levelShift} />}
                  {levelPages.map(renderLevelPages)}
                </Fragment>
              )
            })}
        </ul>
      </nav>
    </section>
  )
}

const Level = ({ value }: { value: string | number }): React.ReactElement => {
  const topicColorTextClass: string = useAppSelector(selectVideoTopicColor).textClass
  return (
    <div className={`flex justify-center ${topicColorTextClass}`}>
      <span className="w-8 h-8 mr-1.5 border rounded-full text-xs leading-none flex items-center justify-center">
        {value}
      </span>
    </div>
  )
}

export default ActivityNavigation
